import React from "react";
import { Drawer, Input, Select, Radio, Switch, Button, message } from "antd";
import Axios from "axios";
import {
  ALERT_ROLES,
  COMMENT_TYPE_GROUPS,
  NO_TEMPLATE,
  dayOptions,
  hourOptions,
  timeOptions,
} from "../alertHelpers";

const { Option, OptGroup } = Select;

const emptyForm = {
  title: "",
  rule: NO_TEMPLATE,
  trigger_mode: "after",
  days: 1,
  hours: 0,
  exact_times: [],
  working_days: true,
  role: "customer_service",
  comment_state_ids: [],
  active: true,
};

const formFromAlert = (alert) => ({
  title: alert.title || "",
  rule: alert.rule || NO_TEMPLATE,
  trigger_mode: alert.trigger_mode || "after",
  days: Number(alert.days) || 0,
  hours: Number(alert.hours) || 0,
  exact_times: alert.exact_times || [],
  working_days: alert.working_days !== false,
  role: alert.role || "customer_service",
  comment_state_ids: alert.comment_state_ids || [],
  active: !!alert.active,
});

function AlertEditDrawer({ visible, alert, rules, commentStates, onClose, onSaved }) {
  const [form, setForm] = React.useState(emptyForm);
  const [saving, setSaving] = React.useState(false);

  React.useEffect(() => {
    if (!visible) return;
    setForm(alert ? formFromAlert(alert) : emptyForm);
  }, [visible, alert]);

  const change = (name, value) => setForm((prev) => ({ ...prev, [name]: value }));

  const isCustom = form.rule === NO_TEMPLATE;

  const validate = () => {
    if (form.trigger_mode === "exact" && form.exact_times.length === 0) {
      message.error("Please select at least one trigger time");
      return false;
    }
    if (form.trigger_mode === "after" && Number(form.days) * 24 + Number(form.hours) <= 0) {
      message.error("Trigger delay must be greater than zero");
      return false;
    }
    if (isCustom && form.comment_state_ids.length === 0) {
      message.error("Please select at least one comment status or pick a template");
      return false;
    }
    return true;
  };

  const submit = () => {
    if (!validate()) return;

    const payload = {
      alert: {
        title: form.title,
        rule: isCustom ? "" : form.rule,
        trigger_mode: form.trigger_mode,
        days: form.trigger_mode === "after" ? form.days : 0,
        hours: form.trigger_mode === "after" ? form.hours : 0,
        exact_times: form.trigger_mode === "exact" ? form.exact_times : [],
        working_days: form.working_days,
        role: form.role,
        comment_state_ids: isCustom ? form.comment_state_ids : [],
        active: form.active,
      },
    };

    setSaving(true);
    const request = alert
      ? Axios.put(`/api/v1/alerts/${alert.id}`, payload)
      : Axios.post("/api/v1/alerts", payload);

    request
      .then(() => {
        message.success(alert ? "Alert updated successfully" : "Alert created successfully");
        onSaved();
      })
      .catch((error) => {
        message.error(
          error?.response?.data?.message || `Failed to ${alert ? "update" : "create"} alert`
        );
      })
      .finally(() => setSaving(false));
  };

  const groupedStates = COMMENT_TYPE_GROUPS.map((group) => ({
    ...group,
    states: commentStates.filter((state) => state.comment_type === group.type),
  })).filter((group) => group.states.length > 0);

  return (
    <Drawer
      title={
        <span style={{ fontSize: 18, fontWeight: 600 }}>
          {alert ? "Edit alert" : "Create alert"}
        </span>
      }
      placement="right"
      width={620}
      visible={visible}
      onClose={onClose}
      bodyStyle={{ paddingBottom: 80 }}
      footer={
        <div className="d-flex justify-content-end">
          <Button onClick={onClose} className="mr-2">
            Back
          </Button>
          <Button type="primary" loading={saving} onClick={submit}>
            {alert ? "Update" : "Create"}
          </Button>
        </div>
      }
    >
      <div className="form-group">
        <label className="text-sm-label">Alert title</label>
        <Input
          value={form.title}
          placeholder="Alert title"
          onChange={(e) => change("title", e.target.value)}
        />
      </div>

      <div className="form-group">
        <label className="text-sm-label">Templates</label>
        <Select
          className="w-100"
          value={form.rule}
          onChange={(value) => change("rule", value)}
          getPopupContainer={(trigger) => trigger.parentNode}
        >
          <Option value={NO_TEMPLATE}>No template</Option>
          {rules.map((rule) => (
            <Option key={rule.value} value={rule.value}>
              {rule.label}
            </Option>
          ))}
        </Select>
      </div>

      <div className="form-group">
        <label className="text-sm-label">Trigger Alert after</label>
        <div className="mb-2">
          <Radio.Group
            value={form.trigger_mode}
            onChange={(e) => change("trigger_mode", e.target.value)}
          >
            <Radio value="after">Alert after time</Radio>
            <Radio value="exact">Alert exact time</Radio>
          </Radio.Group>
        </div>

        {form.trigger_mode === "after" ? (
          <div className="d-flex">
            <Select
              className="w-100 mr-2"
              value={form.days}
              onChange={(value) => change("days", value)}
              getPopupContainer={(trigger) => trigger.parentNode}
            >
              {dayOptions.map((item) => (
                <Option key={item.value} value={item.value}>
                  {item.label}
                </Option>
              ))}
            </Select>
            <Select
              className="w-100"
              value={form.hours}
              onChange={(value) => change("hours", value)}
              getPopupContainer={(trigger) => trigger.parentNode}
            >
              {hourOptions.map((item) => (
                <Option key={item.value} value={item.value}>
                  {item.label}
                </Option>
              ))}
            </Select>
          </div>
        ) : (
          <Select
            mode="multiple"
            className="w-100"
            placeholder="Select time"
            value={form.exact_times}
            onChange={(value) => change("exact_times", value)}
            maxTagCount={8}
            getPopupContainer={(trigger) => trigger.parentNode}
          >
            {timeOptions.map((item) => (
              <Option key={item.value} value={item.value}>
                {item.label}
              </Option>
            ))}
          </Select>
        )}

        <div className="d-flex align-items-center mt-3">
          <Switch
            checked={form.working_days}
            onChange={(checked) => change("working_days", checked)}
          />
          <span className="ml-2">Only working days</span>
        </div>
      </div>

      {isCustom && (
        <div className="form-group">
          <label className="text-sm-label">Assign alert for role</label>
          <div className="row">
            {ALERT_ROLES.map((role) => (
              <div className="col-4" key={role.value}>
                <div
                  className="card-type ant-input cursor-pointer d-flex align-items-center"
                  style={{
                    height: 40,
                    borderColor: form.role === role.value ? "#03B0F7" : "",
                    backgroundColor: form.role === role.value ? "#EDF9FE" : "",
                  }}
                  onClick={() => change("role", role.value)}
                >
                  <Radio
                    className={form.role === role.value ? "selected-radio" : ""}
                    style={{ color: form.role === role.value ? "#03B0F7" : "" }}
                    checked={form.role === role.value}
                    value={role.value}
                  >
                    {role.label}
                  </Radio>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {isCustom && (
        <div className="form-group">
          <label className="text-sm-label">Comment statuses</label>
          <Select
            mode="multiple"
            className="w-100"
            placeholder="Comment type"
            optionFilterProp="label"
            value={form.comment_state_ids}
            onChange={(value) => change("comment_state_ids", value)}
            getPopupContainer={(trigger) => trigger.parentNode}
          >
            {groupedStates.map((group) => (
              <OptGroup key={group.type} label={group.label}>
                {group.states.map((state) => (
                  <Option key={state.id} value={state.id} label={state.name}>
                    {state.name}
                  </Option>
                ))}
              </OptGroup>
            ))}
          </Select>
        </div>
      )}

      <div className="d-flex align-items-center">
        <Switch checked={form.active} onChange={(checked) => change("active", checked)} />
        <span className="ml-2">Active</span>
      </div>
    </Drawer>
  );
}

export default AlertEditDrawer;
